import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
} from "@mui/material";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useWebSocket } from "../../../../contexts/WebSocketContext";
import { useGenerationErrorHandling } from "../../../errorHandling";
import { trackPlatformUsed } from "../../../trackers";
import { GenerationButton } from "../../FeaturesTemplate/FeaturesFields/GenerationButton";
import GlobalTextField from "../../FeaturesTemplate/FeaturesFields/GlobalTextField";
import { globalValidationConfig } from "../../SidePanel/formik-function/helpers";
import RewriteModal from "../../SidePanel/global-rewrite-modal/rewrite-modal";

import { useState } from "react";
import { useSelector } from "react-redux";
import { getSessionUUID } from "../../../../utils/uuidManager";
import { useGeneratePostConclusionMutation } from "../../../../redux/slices/featuresApiSlice";

const ConclusionModal = ({ setOpen, isConclusionGenerated }) => {
  const { t } = useTranslation();
  const [generatePostConclusion, { isLoading }] =
    useGeneratePostConclusionMutation();
  const handleGenerationError = useGenerationErrorHandling();
  const formikProps = useFormikContext();
  const [customize, setCustomize] = useState(false);
  const userInfo = useSelector((state) => state?.auth.userInfo);

  const {
    concluHeading,
    setOpenConclusionModal,
    openConclusionRewrite,
    setOpenConclusionRewrite,
    bufferRef,
  } = useWebSocket();

  const handleGenerate = async () => {
    trackPlatformUsed("conclusion", "ai_generation");

    // Reset buffer before starting new generation
    bufferRef.current = "";

    try {
      let payload = {
        id:
          formikProps.values.id === null || formikProps.values.id === ""
            ? null
            : formikProps.values.id,
        style_id:
          formikProps.values.style_id === null ||
            formikProps.values.style_id === ""
            ? null
            : formikProps.values.style_id,
        company_id:
          formikProps.values.company_id === null ||
            formikProps.values.company_id === ""
            ? null
            : formikProps.values.company_id,
        product_id:
          formikProps.values.product_id === null ||
            formikProps.values.product_id === ""
            ? null
            : formikProps.values.product_id,
        subject: formikProps.values.subject,
        seo_request: formikProps.values.seo_request,
        outline: formikProps.values.outline,
        length: formikProps.values.conclusion_length,
        transition_article: formikProps.values.transition_article,
      };

      if (isConclusionGenerated) {
        payload["conclusion"] = formikProps.values.conclusion;
      } else {
        concluHeading.current = formikProps.values.conclusion;
      }

      // Add customize_prompt if customize is true
      if (customize && formikProps.values.customize_prompt?.trim()) {
        payload["customize_prompt"] = formikProps.values.customize_prompt;
      }

      console.log("Starting conclusion generation with streaming...");

      const uuid = userInfo?.uuid || getSessionUUID();
      const res = await generatePostConclusion({
        ...payload,
        uuid,
        stream: true,
      }).unwrap();

      if (res?.status !== 200) {
        toast.error(
          t(
            "something-went-wrong-with-generation",
            "Une erreur s'est produite lors de la génération"
          )
        );
        return;
      }

      // For streaming, the content will be handled by WebSocket context
      // Only handle non-streaming fallback here
      if (!res?.stream && res?.data) {
        if (!isConclusionGenerated) {
          const zone = document.getElementById("conclusionzone");
          if (zone) {
            zone.innerHTML = concluHeading.current + res?.data;
          }
        } else {
          formikProps.setFieldValue("conclusion_rewrite", res?.data);
        }

        concluHeading.current = "";
      }
    } catch (error) {
      handleGenerationError(error);
      toast.error("Conclusion generation failed. Please try again.");
    }
  };

  const saveSelectedVersion = (data) => {
    const zone = document.getElementById("conclusionzone");
    if (zone) {
      zone.innerHTML = data;
    }
    formikProps.setFieldValue("conclusion_rewrite", "");
    setOpenConclusionRewrite(false);
    setOpenConclusionModal(false);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        //overflow: { md: 'unset', xs: 'scroll' },
        height: "100vh",
        width: {
          lg: "calc(100vw - 500px)",
          md: "calc(100vw - 350px)",
          xs: "100%",
        },
        display: "flex",
        mt: { md: "48px !important", xs: "unset" },
        left: 0,
        top: 0,
        bgcolor: "#000000a1",
        zIndex: 99999,
      }}
    >
      {openConclusionRewrite ? (
        <RewriteModal
          previous={formikProps.values.conclusion}
          rewritten={formikProps.values.conclusion_rewrite}
          save={saveSelectedVersion}
          label={t("Rewrite-conclusion", "Rewrite the conclusion")}
          setOpenModal={setOpenConclusionRewrite}
          rewriteFieldName="conclusion_rewrite"
        />
      ) : (
        <div className="m-auto bg-white rounded-xl w-[80%] max-w-[500px]">
          <div className="flex justify-between p-4 items-center border-b-[3px] border-slate-200">
            <p>
              {!isConclusionGenerated
                ? t("Generate-conclusion", "Generate the conclusion")
                : t("Rewrite-conclusion", "Rewrite the conclusion")}
            </p>
            <Close
              onClick={() => {
                // reset toggle + input + validation state
                setCustomize(false);
                formikProps.setFieldValue("customize_prompt", "");
                formikProps.setFieldTouched("customize_prompt", false);

                setOpen(false);
              }}
              className="cursor-pointer"
              sx={{ fontSize: 32 }}
            />
          </div>
          <div className="p-4">
            <FormControl sx={{ mb: 2 }} fullWidth>
              <p className="mb-2">
                {t("Conclusion-length", "Conclusion length")}
              </p>
              <Select
                sx={{ bgcolor: "white" }}
                value={formikProps.values.conclusion_length}
                onChange={(e) =>
                  formikProps.setFieldValue("conclusion_length", e.target.value)
                }
              >
                <MenuItem value={"courte"}>{t("Short", "Courte")}</MenuItem>
                <MenuItem value={"moyenne"}>{t("Medium", "Moyenne")}</MenuItem>
                <MenuItem value={"longue"}>{t("Long", "Longue")}</MenuItem>
              </Select>
            </FormControl>

            <div>
              <p>
                {t(
                  "Transition-article-for-conclusion",
                  "Article de transition pour la conclusion"
                )}
              </p>
              <GlobalTextField
                style={{ bgcolor: "white" }}
                formikProps={formikProps}
                name={"transition_article"}
                placeholder={
                  "Ex: " +
                  t(
                    "placeholder-transition-article",
                    "un article de blog sur les cocons sémantiques...'"
                  )
                }
                isRequired={globalValidationConfig.transition_article.required}
                maxLength={globalValidationConfig.transition_article.max}
                multipleLines={true}
              />
            </div>
            <div className="pt-0 px-0">
              <div className="mt-2 inline-flex items-center h-6">
                <label className="inline-flex items-center cursor-pointer relative">
                  <input
                    type="checkbox"
                    name="customize"
                    checked={customize}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCustomize(checked);

                      // ✅ clear input when turning off
                      if (!checked) {
                        formikProps.setFieldValue("customize_prompt", "");
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border"></div>
                </label>
                <span className="ms-3 text-sm">
                  {t("custom-instructions", "Instructions personnalisées")}
                </span>
              </div>
              {customize && (
                <Grid item lg={12} xs={12}>
                  <p>
                    {t("Instruction", "Instruction")}
                    <span className="text-red-500 ml-1">*</span>
                  </p>
                  <GlobalTextField
                    formikProps={formikProps}
                    name={"customize_prompt"}
                    placeholder={t(
                      "Ex: Make it more formal",
                      "Ex: Rends-le plus formel"
                    )}
                    isRequired={true}
                    multipleLines={true}
                    maxLength={1000}
                  />
                </Grid>
              )}
            </div>
            <div className="flex items-center mt-4 gap-4">
              <GenerationButton
                formikProps={formikProps}
                text={t("Generate-conclusion", "Générer une conclusion")}
                handleGenerate={handleGenerate}
                isLoading={isLoading}
                platform="openai"
                customize={customize}
              />

              {formikProps.values.conclusion_rewrite !== "" ? (
                <Button
                  variant="contained"
                  className="!bg-violet-400 hover:!bg-violet-500 text-white !normal-case"
                  onClick={() => setOpenConclusionRewrite(true)}
                >
                  {t("View-rewritten-version", "Voir la version réécrite")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default ConclusionModal;
